import {Dispatch, FC, SetStateAction, useState} from 'react'
import {isNotEmpty, numberWithCommas} from '../../../../../../../_metronic/helpers'
import {InvoiceDiscountForm, InvoiceRowForm} from '../../core/_models'
import {CustomInputs} from '../../../../../../../_metronic/helpers/components/CustomInputs'
import {InvoiceItemEditModal} from '../item/InvoiceItemEditModal'
import {InvoiceDiscountEditModal} from '../discount/InvoiceDiscountEditModal'
import {useListView} from '../../core/ListViewProvider'
import {Row} from './Row'
import {getInvoicePatternValidationError} from '../../core/invoicePatternValidation'
import {
  canAddInvoiceRow,
  InvoiceRowPolicyV79,
  InvoiceRulesV79,
  isInvoiceFieldReadOnly,
  isInvoiceFieldVisible,
} from '../../core/invoiceRulesV79'
import {InvoiceSubject, isSalesReturnInvoiceSubject} from '../../core/invoiceSubject'
import {isTemplate11} from '../../core/invoiceTemplate11'
import {
  InvoiceEmptyState,
  InvoiceFinancialSummary,
  InvoiceSectionHeader,
  InvoiceSummaryItem,
} from '../components/InvoicePresentation'
import {CodelessUnitRowsNotice} from '../components/CodelessUnitRowsNotice'

type props = {
  items: {
    items: InvoiceRowForm[]
    setItems: Dispatch<SetStateAction<InvoiceRowForm[]>>
  }
  returnQty: {
    returnQty: InvoiceRowForm[]
    setReturnQty: Dispatch<SetStateAction<InvoiceRowForm[]>>
  }
  discounts: {
    discounts: InvoiceDiscountForm[]
    setDiscounts: Dispatch<SetStateAction<InvoiceDiscountForm[]>>
  }
  getSumPrice: () => number
  getSumTaxPrice: () => number
  getSumVatPrice: () => number
  getSumItemDiscount: () => number
  getSumItemTotalDiscount: () => number
  getSumDiscount: () => number
  getSumAdPrice: () => number
  getSumNetPrice: () => number
  getSumAdPriceEXDiscount: () => number
  updateItemsKey: number
  invoiceTemplate: number
  invoiceType: number
  invoiceSubject: InvoiceSubject
  invoiceDate: string
  formType: number
  tax17?: {
    tax17: string
    setTax17: Dispatch<SetStateAction<string>>
  }
  rowPolicy?: InvoiceRowPolicyV79
  rules?: InvoiceRulesV79
  /** The environment the invoice goes to (undefined: the business default). */
  productionInvoice?: boolean
}

const Step2: FC<props> = ({
  items,
  discounts,
  getSumPrice,
  getSumTaxPrice,
  getSumVatPrice,
  getSumItemDiscount,
  getSumItemTotalDiscount,
  getSumDiscount,
  getSumAdPrice,
  getSumNetPrice,
  getSumAdPriceEXDiscount,
  updateItemsKey,
  returnQty,
  invoiceTemplate,
  invoiceType,
  invoiceSubject,
  invoiceDate,
  formType,
  tax17,
  rowPolicy,
  rules,
  productionInvoice,
}) => {
  const {variableD} = useListView()
  const [editInvoiceItem, setEditInvoiceItem] = useState<InvoiceRowForm | null | undefined>(
    undefined
  )
  const [editInvoiceDiscount, setEditInvoiceDiscount] = useState<
    InvoiceDiscountForm | null | undefined
  >(undefined)
  const patternValidationError = getInvoicePatternValidationError(
    invoiceTemplate,
    items.items,
    rowPolicy
  )
  const maximumRows = rowPolicy
    ? rowPolicy.maximumRows
    : [11, 14].includes(invoiceTemplate)
    ? 1
    : undefined
  const amountMustEqualOne = rowPolicy
    ? rowPolicy.quantityConstraint === 'AmountEqualsOne'
    : [6, 8].includes(invoiceTemplate)
  const canAddInvoiceItem = rowPolicy
    ? canAddInvoiceRow(rowPolicy, items.items.length)
    : !maximumRows || items.items.length < maximumRows
  const canShowAddInvoiceItem = variableD != 4 && variableD != 2
  const isSalesReturn = isSalesReturnInvoiceSubject(invoiceSubject)
  const showDiscount =
    discounts.discounts.length > 0 ||
    items.items.some((item) => !!item.discount || !!item.totalDiscount)
  const showTax =
    isInvoiceFieldVisible(rules, 'vam', true) ||
    items.items.some(
      (item) => !!item.vatPrice || !!item.taxPrice || !!item.govTaxPrice || !!item.vatRate
    )
  const columnCount = 6 + (showDiscount ? 1 : 0) + (showTax ? 1 : 0)

  const deleteInvoiceDiscount = (rw: InvoiceDiscountForm) => {
    if (discounts.discounts) {
      if (window.confirm('آیا از حذف تخفیف اطمینان دارید؟')) {
        const ind = discounts.discounts.findIndex((x) => x.id == rw.id)
        discounts.discounts.splice(ind, 1)
        discounts.setDiscounts([...[], ...discounts.discounts])
      }
    }
  }

  const deleteInvoiceItem = (rw: InvoiceRowForm) => {
    if (rowPolicy?.readOnlyBody === true || rowPolicy?.allowDelete === false) {
      window.alert('اقلام صورتحساب ابطالی از صورتحساب مرجع دریافت شده‌اند و قابل تغییر نیستند.')
      return
    }
    if (rowPolicy && items.items.length <= rowPolicy.minimumRows) {
      window.alert(`این صورتحساب باید حداقل ${rowPolicy.minimumRows} ردیف داشته باشد.`)
      return
    }
    if (window.confirm('آیا از حذف ردیف اطمینان دارید؟')) {
      const ind = items.items.findIndex((x) => x.id == rw.id)
      items.items.splice(ind, 1)
      items.setItems([...[], ...items.items])
    }
  }

  const sumPrice = getSumPrice()
  const sumDiscount = Number(getSumItemTotalDiscount()) + Number(getSumItemDiscount())
  const sumAfterDiscount = getSumAdPrice()
  const sumVat = getSumVatPrice()
  const sumOtherTax = getSumTaxPrice()
  const sumNetPrice = getSumNetPrice()
  const summaryItems: InvoiceSummaryItem[] = [
    {
      key: 'item-count',
      label: 'تعداد ردیف‌های صورتحساب',
      value: numberWithCommas(items.items.length),
    },
    {
      key: 'before-discount',
      label: 'مجموع مبلغ قبل از کسر تخفیف',
      value: numberWithCommas(sumPrice),
      unit: 'ریال',
    },
  ]
  if (sumDiscount !== 0) {
    summaryItems.push({
      key: 'discount',
      label: 'مجموع تخفیفات',
      value: numberWithCommas(sumDiscount),
      unit: 'ریال',
      tone: 'success',
    })
  }
  summaryItems.push({
    key: 'after-discount',
    label: 'مجموع مبلغ پس از کسر تخفیف',
    value: numberWithCommas(sumAfterDiscount),
    unit: 'ریال',
  })
  if (isInvoiceFieldVisible(rules, 'tvam', true)) {
    summaryItems.push({
      key: 'vat',
      label: 'مجموع مالیات بر ارزش افزوده',
      value: numberWithCommas(sumVat),
      unit: 'ریال',
    })
  }
  if (sumOtherTax !== 0 && isInvoiceFieldVisible(rules, 'todam', true)) {
    summaryItems.push({
      key: 'other-tax',
      label: 'مجموع سایر مالیات، عوارض و وجوه قانونی',
      value: numberWithCommas(sumOtherTax),
      unit: 'ریال',
    })
  }
  summaryItems.push({
    key: 'net',
    label: 'مجموع صورتحساب',
    value: numberWithCommas(sumNetPrice),
    unit: 'ریال',
    emphasis: true,
  })

  const addInvoiceItemButton = canShowAddInvoiceItem ? (
    <button
      type='button'
      disabled={!canAddInvoiceItem}
      className={`btn btn-sm ${canAddInvoiceItem ? 'btn-primary' : 'btn-light-secondary'}`}
      onClick={() => canAddInvoiceItem && setEditInvoiceItem(null)}
    >
      <i className='las la-plus' aria-hidden='true'></i>
      {canAddInvoiceItem
        ? 'افزودن قلم'
        : rowPolicy?.message ||
          (maximumRows ? `حداکثر ${maximumRows} ردیف مجاز است` : 'افزودن قلم مجاز نیست')}
    </button>
  ) : undefined

  return (
    <>
      <section className='mp-invoice-section' aria-labelledby='invoice-items-title'>
        <InvoiceSectionHeader
          title={<span id='invoice-items-title'>اقلام صورتحساب</span>}
          helpKey='section.body'
          isRequired
          description='اطلاعات اصلی هر قلم در جدول نمایش داده می‌شود؛ فیلدهای تخصصی در جزئیات همان ردیف در دسترس‌اند.'
          eyebrow={`${numberWithCommas(items.items.length)} ردیف`}
          actions={addInvoiceItemButton}
        />
        {rowPolicy?.readOnlyBody === true && (
          <div className='alert alert-info d-flex align-items-center p-4 mb-4'>
            <i className='las la-link fs-2x text-info me-3' aria-hidden='true'></i>
            <div>
              <span className='badge badge-light-info me-2'>دریافت‌شده از صورتحساب مرجع</span>
              اقلام صورتحساب ابطالی فقط برای مشاهده هستند و در JSON ارسالی بدنه‌ای ارسال نمی‌شود.
            </div>
          </div>
        )}
        {patternValidationError != '' && (
          <div className='alert alert-danger d-flex align-items-center p-4 mb-4' role='alert'>
            <i
              className='las la-exclamation-triangle fs-2x text-danger me-3'
              aria-hidden='true'
            ></i>
            <div className='fw-bold'>{patternValidationError}</div>
          </div>
        )}
        <CodelessUnitRowsNotice items={items.items} />
        <div className='mp-invoice-data-table-wrap'>
          <table
            className='table mp-invoice-data-table mp-invoice-items-table'
            key={updateItemsKey}
          >
            <thead>
              <tr>
                <th className='mp-invoice-col--index text-center'>ردیف</th>
                <th className='mp-invoice-col--item text-start'>کالا یا خدمت</th>
                <th className='mp-invoice-col--number text-center'>تعداد یا مقدار</th>
                <th className='mp-invoice-col--money text-center'>مبلغ واحد</th>
                {showDiscount && <th className='mp-invoice-col--money text-center'>تخفیف</th>}
                {showTax && <th className='mp-invoice-col--tax text-center'>مالیات و عوارض</th>}
                <th className='mp-invoice-col--final text-center'>مبلغ نهایی</th>
                <th className='mp-invoice-col--actions text-center'>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {items.items.map((rw, ind) => (
                <Row
                  key={rw.id || ind}
                  rw={rw}
                  ind={ind}
                  setEditInvoiceItem={setEditInvoiceItem}
                  deleteInvoiceItem={deleteInvoiceItem}
                  returnQty={returnQty}
                  invoiceTemplate={invoiceTemplate}
                  invoiceDate={invoiceDate}
                  readOnly={rowPolicy?.readOnlyBody === true}
                  showDiscount={showDiscount}
                  showTax={showTax}
                  columnCount={columnCount}
                  isEditing={editInvoiceItem?.id === rw.id}
                />
              ))}
              {items.items.length === 0 && (
                <tr className='mp-invoice-data-empty-row'>
                  <td colSpan={columnCount}>
                    <InvoiceEmptyState
                      title='هنوز قلمی ثبت نشده است'
                      description='برای تکمیل صورتحساب، اولین کالا یا خدمت را اضافه کنید.'
                      icon='la-box-open'
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <InvoiceFinancialSummary
        title='خلاصه مبالغ اقلام'
        description='مبالغ به‌ترتیب روند محاسبات فعلی صورتحساب نمایش داده شده‌اند.'
        items={summaryItems}
        warning={patternValidationError || undefined}
      />

      {!isSalesReturn &&
        (invoiceTemplate == 7 ||
          isInvoiceFieldVisible(rules, 'tax17', ![7, 11].includes(invoiceTemplate))) && (
          <section className='mp-invoice-section mp-invoice-section--compact'>
            <InvoiceSectionHeader
              title='مقادیر تکمیلی صورتحساب'
              description='این مقادیر فقط برای الگوی انتخاب‌شده نمایش داده می‌شوند.'
            />
            <div className='row mp-invoice-related-fields'>
              {invoiceTemplate == 7 && (
                <>
                  <div className='col-md-4 col-12'>
                    <CustomInputs type='text' label='مجموع ارزش ریالی' LTR isReadonly isDisabled />
                  </div>
                  <div className='col-md-4 col-12'>
                    <CustomInputs type='text' label='مجموع ارزش ارزی' LTR isReadonly isDisabled />
                  </div>
                  <div className='col-md-4 col-12'>
                    <CustomInputs type='text' label='مجموع وزن خالص' LTR isReadonly isDisabled />
                  </div>
                </>
              )}
              {isInvoiceFieldVisible(rules, 'tax17', ![7, 11].includes(invoiceTemplate)) && (
                <div className='col-md-6 col-12'>
                  <CustomInputs
                    type='currency'
                    label='مالیات موضوع ماده ۱۷'
                    helpKey='header.tax17'
                    stateValue={tax17 ? {value: tax17.tax17, setValue: tax17.setTax17} : undefined}
                    isReadonly={isInvoiceFieldReadOnly(rules, 'tax17')}
                    hint='اختیاری است؛ مبلغ آن نباید از مجموع مالیات بر ارزش افزوده صورتحساب بیشتر شود.'
                  />
                </div>
              )}
            </div>
          </section>
        )}

      {!isSalesReturn && !isTemplate11(invoiceType, invoiceTemplate) && (
        <section className='mp-invoice-section mp-invoice-section--compact'>
          <InvoiceSectionHeader
            title='سایر تخفیف‌های کلی صورتحساب'
            helpKey='ui.discountDescription'
            description='تخفیف‌هایی که به کل صورتحساب تعلق دارند، جدا از تخفیف هر قلم ثبت می‌شوند.'
            eyebrow={`${numberWithCommas(discounts.discounts.length)} مورد`}
            actions={
              variableD != 4 ? (
                <button
                  type='button'
                  disabled={!(getSumAdPriceEXDiscount() > 0)}
                  className='btn btn-sm btn-light-primary'
                  onClick={() => setEditInvoiceDiscount(null)}
                >
                  <i className='las la-plus' aria-hidden='true'></i>
                  افزودن تخفیف
                </button>
              ) : undefined
            }
          />
          <div className='mp-invoice-data-table-wrap'>
            <table className='table mp-invoice-data-table mp-invoice-discounts-table'>
              <thead>
                <tr>
                  <th className='mp-invoice-col--index text-center'>ردیف</th>
                  <th className='text-start'>شرح تخفیف</th>
                  <th className='text-center'>درصد</th>
                  <th className='text-center'>مبلغ</th>
                  <th className='mp-invoice-col--actions text-center'>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {discounts.discounts.map((rw, ind) => (
                  <tr className='mp-invoice-data-row' key={rw.id || ind}>
                    <td className='mp-invoice-cell--index text-center' data-label='ردیف'>
                      {ind + 1}
                    </td>
                    <td className='mp-invoice-cell--item text-start' data-label='شرح تخفیف'>
                      {rw.description}
                    </td>
                    <td className='text-center' data-label='درصد'>
                      {isNotEmpty(rw.rate) ? `${rw.rate}٪` : '—'}
                    </td>
                    <td className='mp-invoice-cell--money text-center' data-label='مبلغ'>
                      <b>{numberWithCommas(rw.price)}</b>
                      <small>ریال</small>
                    </td>
                    <td className='mp-invoice-cell--actions text-center' data-label='عملیات'>
                      <div className='mp-invoice-row-actions'>
                        <button
                          type='button'
                          className='btn btn-icon btn-sm btn-light-info'
                          title='ویرایش تخفیف'
                          aria-label='ویرایش تخفیف'
                          onClick={() => setEditInvoiceDiscount(rw)}
                        >
                          <i className='las la-edit' aria-hidden='true'></i>
                        </button>
                        <button
                          type='button'
                          className='btn btn-icon btn-sm btn-light-danger'
                          title='حذف تخفیف'
                          aria-label='حذف تخفیف'
                          onClick={() => deleteInvoiceDiscount(rw)}
                        >
                          <i className='las la-trash-alt' aria-hidden='true'></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {discounts.discounts.length === 0 && (
                  <tr className='mp-invoice-data-empty-row'>
                    <td colSpan={5}>
                      <InvoiceEmptyState
                        title='تخفیف کلی ثبت نشده است'
                        description='در صورت نیاز، تخفیف کلی صورتحساب را از این بخش اضافه کنید.'
                        icon='la-tags'
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className='mp-invoice-section-total'>
            <span>مجموع تخفیف‌های کلی</span>
            <b>{numberWithCommas(Number(getSumDiscount()))} ریال</b>
          </div>
        </section>
      )}

      {editInvoiceItem !== undefined && (
        <InvoiceItemEditModal
          items={items}
          invoiceItem={{invoiceItem: editInvoiceItem, setInvoiceItem: setEditInvoiceItem}}
          invoiceTemplate={invoiceTemplate}
          invoiceType={invoiceType}
          invoiceSubject={invoiceSubject}
          invoiceDate={invoiceDate}
          formType={formType}
          amountMustEqualOne={amountMustEqualOne}
          rules={rules}
          productionInvoice={productionInvoice}
        />
      )}
      {!isSalesReturn && editInvoiceDiscount !== undefined && (
        <InvoiceDiscountEditModal
          discounts={discounts}
          discountItem={{
            discountItem: editInvoiceDiscount,
            setDiscountItem: setEditInvoiceDiscount,
          }}
          sumAdPrice={getSumAdPrice() + (editInvoiceDiscount ? editInvoiceDiscount.price : 0)}
        />
      )}
    </>
  )
}

export {Step2}
